'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import * as Sentry from '@sentry/nextjs';

export default function RootError({
    error,
    reset,
}: {
    error: Error & { digest?: string };
    reset: () => void;
}) {
    const router = useRouter();

    useEffect(() => {
        Sentry.captureException(error);
    }, [error]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-secondary/30 p-6">
            <div className="w-full max-w-md text-center">
                {/* Animated floating icon */}
                <div className="mx-auto mb-6 relative">
                    <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-lg shadow-primary/20 animate-[bounce_3s_ease-in-out_infinite]">
                        <svg
                            className="w-10 h-10 text-primary-foreground"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                            <line x1="12" y1="9" x2="12" y2="13" />
                            <line x1="12" y1="17" x2="12.01" y2="17" />
                        </svg>
                    </div>
                </div>

                {/* Card */}
                <div className="bg-card rounded-2xl border shadow-sm p-8">
                    <h1 className="text-2xl font-bold text-foreground mb-3">
                        Oops! Something went wrong
                    </h1>

                    <p className="text-muted-foreground text-sm leading-relaxed mb-8">
                        Don&apos;t worry — this error has been automatically reported and
                        our team is already looking into it. We&apos;ll have things fixed
                        shortly.
                    </p>

                    <div className="flex items-center justify-center gap-3">
                        <button
                            onClick={() => reset()}
                            className="px-5 py-2.5 text-sm font-semibold text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 transition-all hover:shadow-md hover:shadow-primary/25 active:scale-[0.98] cursor-pointer"
                        >
                            Try Again
                        </button>
                        <button
                            onClick={() => router.push('/')}
                            className="px-5 py-2.5 text-sm font-semibold text-primary bg-primary/10 rounded-lg hover:bg-primary/15 transition-all active:scale-[0.98] cursor-pointer"
                        >
                            Go Home
                        </button>
                    </div>

                    {process.env.NODE_ENV === 'development' && error?.message && (
                        <details className="mt-6 text-left p-3 bg-destructive/5 rounded-lg text-xs text-destructive">
                            <summary className="cursor-pointer font-semibold">
                                Error Details (dev only)
                            </summary>
                            <pre className="mt-2 whitespace-pre-wrap break-words">
                                {error.message}
                            </pre>
                        </details>
                    )}
                </div>

                <p className="mt-4 text-xs text-muted-foreground/60">
                    Error ID: {error?.digest || 'N/A'}
                </p>
            </div>
        </div>
    );
}
